import React, { useEffect, useState, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { useUserStore, useGamesStore, useHighlightsStore, usePostsStore } from '../stores/StoreProvider';
import { User, Settings, Trophy, Calendar, LogOut, Plus, Share2, Copy, Share } from 'lucide-react';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import VideoPreview from '../components/VideoPreview';
import HighlightUpload from '../components/HighlightUpload';

const Profile = observer(() => {
  const { userId } = useParams();
  const user = useUserStore();
  const gamesStore = useGamesStore();
  const highlightsStore = useHighlightsStore();
  const postsStore = usePostsStore();
  const [awards, setAwards] = useState([]);
  const [targetUser, setTargetUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('highlights');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [version, setVersion] = useState(null);

  // Check if we're viewing another user's profile
  const isOwnProfile = !userId || userId === user.profile?.id?.toString();

  // Fetch awards for the displayed user
  const fetchAwards = useCallback(async () => {
    const id = isOwnProfile ? user.profile?.id : targetUser?.id;
    if (!id) return;
    try {
      const response = await user.rootStore.authenticatedApiCall(
        `api/users/${id}/awards`,
        {},
        'GET'
      );
      setAwards(response);
    } catch (error) {
      console.error('Error fetching awards:', error);
      setAwards([]);
    }
  }, [isOwnProfile, user.profile?.id, targetUser?.id, user.rootStore]);

  // Fetch version information
  const fetchVersion = useCallback(async () => {
    try {
      const response = await fetch('/version.json');
      const versionData = await response.json();
      setVersion(versionData);
    } catch (error) {
      console.error('Error fetching version:', error);
      setVersion({ version: 'Unknown', otaVersion: 'Unknown' });
    }
  }, []);

  const fetchTargetUser = useCallback(async () => {
    if (!user.accessToken || !userId) return;
    setLoading(true);
    try {
      const userData = await user.fetchUserById(userId);
      setTargetUser(userData);
      
      // Fetch the target user's games
      await gamesStore.fetchUserGames(userId);
    } catch (error) {
      console.error("Error fetching target user:", error);
      toast.error("Failed to load user profile");
    } finally {
      setLoading(false);
    }
  }, [userId, user, gamesStore]);

  // Fetch target user if viewing someone else's profile
  useEffect(() => {
    if (userId && !isOwnProfile) {
      fetchTargetUser();
    }
  }, [userId, isOwnProfile, fetchTargetUser]);

  // Fetch data on mount
  useEffect(() => {
    if (user.isAuthenticated) {
      if (isOwnProfile) {
        gamesStore.fetchGames();
        highlightsStore.fetchHighlights();
        postsStore.fetchPosts();
        fetchAwards();
        fetchVersion(); // Fetch version for own profile
        // Clear user games when viewing own profile
        gamesStore.clearUserGames();
      } else if (targetUser) {
        gamesStore.fetchUserGames(targetUser.id);
        highlightsStore.fetchHighlights();
        postsStore.fetchPosts();
        fetchAwards();
      }
    }
  }, [user.isAuthenticated, isOwnProfile, targetUser, gamesStore, highlightsStore, postsStore, fetchAwards, fetchVersion]);

  // Cleanup effect - clear user games when component unmounts
  useEffect(() => {
    return () => {
      gamesStore.clearUserGames();
    };
  }, [gamesStore]);

  // Use target user data if viewing someone else's profile, otherwise use current user
  const displayUser = isOwnProfile ? user.profile : targetUser;

  const handleShareProfile = useCallback(() => {
    const profileId = isOwnProfile ? user.profile?.id : targetUser?.id;
    if (!profileId) return;

    const shareUrl = `${window.location.origin}/player/${profileId}`;

    if (navigator.share) {
      navigator.share({
        title: `${displayUser?.first_name} ${displayUser?.last_name}'s Hockey Stats`,
        text: `Check out ${displayUser?.first_name} ${displayUser?.last_name}'s hockey season stats!`,
        url: shareUrl
      });
    } else {
      navigator.clipboard.writeText(shareUrl);
      toast.success('Profile link copied to clipboard!');
    }
  }, [isOwnProfile, user.profile?.id, targetUser?.id, displayUser]);

  const handleCopyProfile = useCallback(() => {
    const profileId = isOwnProfile ? user.profile?.id : targetUser?.id;
    if (!profileId) return;

    const shareUrl = `${window.location.origin}/player/${profileId}`;
    navigator.clipboard.writeText(shareUrl);
    toast.success('Profile link copied to clipboard!');
  }, [isOwnProfile, user.profile?.id, targetUser?.id]);

  // Calculate real stats - use different data based on profile type
  const totalGames = isOwnProfile ? gamesStore.games.length : gamesStore.getUserGames().length;
  const seasonStats = isOwnProfile ? gamesStore.getSeasonStats() : gamesStore.getUserSeasonStats();
  const lastSeasonStats = isOwnProfile ? gamesStore.getLastSeasonStats() : gamesStore.getUserLastSeasonStats();
  const totalHighlights = highlightsStore.highlights.length;
  const totalPosts = postsStore.posts.length;

  const handleVideoPress = (highlight) => {
    setSelectedVideo(highlight);
    setShowVideoModal(true);
  };

  const handleUploadComplete = async () => {
    setShowUploadModal(false);
    await highlightsStore.fetchHighlights();
  };

  const renderGame = (game) => (
    <div key={game.id} className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow">
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1">
          <div className="flex items-center space-x-3 mb-2">
            <div className="text-center">
              <p className="text-lg font-bold text-gray-900">{game.our_score}</p>
              <p className="text-xs text-gray-600 max-w-24">
                {game.userOrganization?.team_name || game.userOrganization?.name || 'Your Team'}
              </p>
            </div>
            <div className="text-gray-400">vs</div>
            <div className="text-center">
              <p className="text-lg font-bold text-gray-900">{game.their_score}</p>
              <p className="text-xs text-gray-600 max-w-24">
                {game.opponent?.team_name || game.opponent?.name || 'Opponent'}
              </p>
            </div>
          </div>
          <p className="text-sm text-gray-600">
            {new Date(game.date).toLocaleDateString()}
          </p>
        </div>
        <div className="text-right">
          <p className={`text-sm font-medium ${
            game.our_score > game.their_score ? 'text-green-600' : 'text-red-600'
          }`}>
            {game.our_score > game.their_score ? 'WIN' : 'LOSS'}
          </p>
        </div>
      </div>
      
      <div className="flex justify-around pt-3 border-t border-gray-100">
        {displayUser?.position === 'goalie' ? (
          // Goalie Stats
          <>
            <div className="text-center">
              <span className="text-xs text-gray-500">Saves</span>
              <p className="text-sm font-semibold text-gray-900">{game.saves || 0}</p>
            </div>
            <div className="text-center">
              <span className="text-xs text-gray-500">GA</span>
              <p className="text-sm font-semibold text-gray-900">{game.goals_against || 0}</p>
            </div>
            <div className="text-center">
              <span className="text-xs text-gray-500">SV%</span>
              <p className="text-sm font-semibold text-gray-900">
                {game.shots_against > 0 ? ((game.saves / game.shots_against) * 100).toFixed(1) : 0}%
              </p>
            </div>
          </>
        ) : (
          // Skater Stats
          <>
            <div className="text-center">
              <span className="text-xs text-gray-500">Goals</span>
              <p className="text-sm font-semibold text-gray-900">{game.goals}</p>
            </div>
            <div className="text-center">
              <span className="text-xs text-gray-500">Assists</span>
              <p className="text-sm font-semibold text-gray-900">{game.assists}</p>
            </div>
            <div className="text-center">
              <span className="text-xs text-gray-500">Points</span>
              <p className="text-sm font-semibold text-gray-900">{game.goals + game.assists}</p>
            </div>
          </>
        )}
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case "highlights":
        return (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-900">
                {isOwnProfile ? "Your Highlights" : `${displayUser?.first_name}'s Highlights`}
              </h2>
              {isOwnProfile && (
                <button
                  onClick={() => setShowUploadModal(true)}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                >
                  <Plus className="mr-2" size={16} />
                  Add Highlight
                </button>
              )}
            </div>

            {highlightsStore.loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-gray-600 mt-2">Loading highlights...</p>
              </div>
            ) : highlightsStore.highlights.length === 0 ? (
              <div className="bg-gray-50 rounded-lg p-8 text-center">
                <p className="text-gray-600">No highlights yet</p>
                <p className="text-sm text-gray-500 mt-1">
                  {isOwnProfile ? "Your best moments will appear here" : "No highlights shared yet"}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {highlightsStore.highlights.map((highlight) => (
                  <VideoPreview
                    key={highlight.id}
                    highlight={highlight}
                    onVideoPress={handleVideoPress}
                  />
                ))}
              </div>
            )}
          </div>
        );

      case "games":
        return (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-900">
              {isOwnProfile ? "Your Games" : `${displayUser?.first_name}'s Games`}
            </h2>

            {gamesStore.loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-gray-600 mt-2">Loading games...</p>
              </div>
            ) : (isOwnProfile ? gamesStore.games : gamesStore.getUserGames()).length === 0 ? (
              <div className="bg-gray-50 rounded-lg p-8 text-center">
                <p className="text-gray-600">No games yet</p>
                <p className="text-sm text-gray-500 mt-1">
                  {isOwnProfile ? "Your games will appear here" : "No games recorded yet"}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {(isOwnProfile ? gamesStore.games : gamesStore.getUserGames()).map(renderGame)}
              </div>
            )}
          </div>
        );

      case "posts":
        return (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-900">
              {isOwnProfile ? "Your Posts" : `${displayUser?.first_name}'s Posts`}
            </h2>

            {postsStore.loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-gray-600 mt-2">Loading posts...</p>
              </div>
            ) : postsStore.posts.length === 0 ? (
              <div className="bg-gray-50 rounded-lg p-8 text-center">
                <p className="text-gray-600">No posts yet</p>
                <p className="text-sm text-gray-500 mt-1">
                  {isOwnProfile ? "Your posts will appear here" : "No posts shared yet"}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {postsStore.posts.map((post) => (
                  <div key={post.id} className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="flex items-start space-x-3">
                      <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                        <User size={20} className="text-gray-600" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <h3 className="font-semibold text-gray-900">
                            {displayUser?.first_name} {displayUser?.last_name}
                          </h3>
                          <span className="text-sm text-gray-500">
                            {new Date(post.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="text-gray-700">{post.content}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">
          {isOwnProfile ? "Profile" : `${displayUser?.first_name}'s Profile`}
        </h1>
        <div className="flex items-center justify-between">
          <p className="text-gray-600">
            {isOwnProfile ? "Manage your account and settings" : "View user profile"}
          </p>
          <div className="flex space-x-2">
            <button
              onClick={handleCopyProfile}
              className="btn-secondary flex items-center space-x-2"
              title="Share Profile Link"
            >
              <Share size={16} />
              <span>Share</span>
            </button>
          </div>
        </div>
      </div>

      {/* Profile Card */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
        <div className="flex items-center space-x-6">
          <div className="w-20 h-20 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
            {displayUser?.avatar ? (
              <img src={displayUser.avatar} alt="Avatar" className="w-20 h-20 rounded-full object-cover" />
            ) : (
              <User className="text-white" size={32} />
            )}
          </div>
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-gray-900">
              {displayUser?.first_name && displayUser?.last_name 
                ? `${displayUser.first_name} ${displayUser.last_name}`
                : displayUser?.email || 'User'
              }
            </h2>
            <p className="text-gray-600">{displayUser?.email}</p>
            {displayUser?.age && displayUser?.level && (
              <p className="text-sm text-gray-500">{displayUser.age} {displayUser.level}</p>
            )}
            {displayUser?.position && (
              <p className="text-sm text-blue-600 capitalize">{displayUser.position}</p>
            )}
            {displayUser?.organization?.name && (
              <p className="text-sm text-blue-600">{displayUser.organization.name}</p>
            )}
          </div>
          {isOwnProfile && (
            <button className="btn-secondary">
              <Settings className="mr-2" size={16} />
              Edit Profile
            </button>
          )}
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Trophy className="text-blue-600" size={24} />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Games Played</p>
              <p className="text-2xl font-bold text-gray-900">{totalGames}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <Calendar className="text-green-600" size={24} />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Win Rate</p>
              <p className="text-2xl font-bold text-gray-900">{seasonStats.winPercentage}%</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <User className="text-purple-600" size={24} />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Highlights</p>
              <p className="text-2xl font-bold text-gray-900">{totalHighlights}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <User className="text-orange-600" size={24} />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Posts</p>
              <p className="text-2xl font-bold text-gray-900">{totalPosts}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Current Season Stats */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">Current Season Stats</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gray-50 rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-blue-600 mb-1">{seasonStats.wins}</div>
            <div className="text-sm text-gray-600">Wins</div>
          </div>
          <div className="bg-gray-50 rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-red-600 mb-1">{seasonStats.losses}</div>
            <div className="text-sm text-gray-600">Losses</div>
          </div>
          {displayUser?.position === 'goalie' ? (
            // Goalie Stats
            <>
              <div className="bg-gray-50 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-green-600 mb-1">{seasonStats.totalSaves}</div>
                <div className="text-sm text-gray-600">Saves</div>
              </div>
              <div className="bg-gray-50 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-purple-600 mb-1">{seasonStats.savePercentage}%</div>
                <div className="text-sm text-gray-600">Save %</div>
              </div>
            </>
          ) : (
            // Skater Stats
            <>
              <div className="bg-gray-50 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-green-600 mb-1">{seasonStats.totalGoals}</div>
                <div className="text-sm text-gray-600">Goals</div>
              </div>
              <div className="bg-gray-50 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-purple-600 mb-1">{seasonStats.totalAssists}</div>
                <div className="text-sm text-gray-600">Assists</div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Last Season Stats */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">Last Season Stats</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gray-50 rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-blue-600 mb-1">{lastSeasonStats.wins}</div>
            <div className="text-sm text-gray-600">Wins</div>
          </div>
          <div className="bg-gray-50 rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-red-600 mb-1">{lastSeasonStats.losses}</div>
            <div className="text-sm text-gray-600">Losses</div>
          </div>
          {displayUser?.position === 'goalie' ? (
            // Goalie Stats
            <>
              <div className="bg-gray-50 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-green-600 mb-1">{lastSeasonStats.totalSaves}</div>
                <div className="text-sm text-gray-600">Saves</div>
              </div>
              <div className="bg-gray-50 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-purple-600 mb-1">{lastSeasonStats.savePercentage}%</div>
                <div className="text-sm text-gray-600">Save %</div>
              </div>
            </>
          ) : (
            // Skater Stats
            <>
              <div className="bg-gray-50 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-green-600 mb-1">{lastSeasonStats.totalGoals}</div>
                <div className="text-sm text-gray-600">Goals</div>
              </div>
              <div className="bg-gray-50 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-purple-600 mb-1">{lastSeasonStats.totalAssists}</div>
                <div className="text-sm text-gray-600">Assists</div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200">
        <div className="flex border-b border-gray-200">
          <button
            className={`flex-1 px-6 py-4 text-center font-medium transition-colors ${
              activeTab === 'highlights'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
            onClick={() => setActiveTab('highlights')}
          >
            Highlights
          </button>
          <button
            className={`flex-1 px-6 py-4 text-center font-medium transition-colors ${
              activeTab === 'games'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
            onClick={() => setActiveTab('games')}
          >
            Games
          </button>
          <button
            className={`flex-1 px-6 py-4 text-center font-medium transition-colors ${
              activeTab === 'posts'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
            onClick={() => setActiveTab('posts')}
          >
            Posts
          </button>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {renderTabContent()}
        </div>
      </div>

      {/* Awards Section */}
      {awards.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Awards</h2>
          </div>
          <div className="p-6">
            <div className="flex space-x-6 overflow-x-auto">
              {awards.map((award) => (
                <div key={award.id} className="flex-shrink-0 text-center w-24">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    {award.award?.image_url ? (
                      <img
                        src={award.award.image_url}
                        alt={award.award.name}
                        className="w-12 h-12 rounded-full object-contain"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : (
                      <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
                        <span className="text-white text-xl font-bold">🏆</span>
                      </div>
                    )}
                  </div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-1 truncate">
                    {award.award?.name || 'Award'}
                  </h3>
                  <p className="text-xs text-gray-600 text-center line-clamp-2">
                    {award.award?.description || ''}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Danger Zone - Only for own profile */}
      {isOwnProfile && (
        <div className="bg-white rounded-2xl border-red-200 shadow-sm border">
          <div className="p-6 border-b border-red-200">
            <h2 className="text-xl font-semibold text-red-900">Danger Zone</h2>
          </div>
          <div className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Delete Account</h3>
                <p className="text-gray-600">Permanently delete your account and all data</p>
              </div>
              <button className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors">
                <LogOut className="mr-2" size={16} />
                Delete Account
              </button>
            </div>
            
            {/* Version Information */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-medium text-gray-700">App Version</h4>
                  <p className="text-sm text-gray-500">
                    {version ? `v${version.version} (${version.otaVersion})` : 'Loading...'}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-400">
                    {version?.buildDate ? new Date(version.buildDate).toLocaleDateString() : ''}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <HighlightUpload onComplete={handleUploadComplete} />
          </div>
        </div>
      )}

      {showVideoModal && selectedVideo && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-4 max-w-4xl w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">{selectedVideo.title}</h3>
              <button
                onClick={() => setShowVideoModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            <video
              src={selectedVideo.media_url}
              controls
              className="w-full h-auto rounded"
            />
          </div>
        </div>
      )}
    </div>
  );
});

export default Profile;